import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Stack,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Alert from "@mui/material/Alert";

import ModalRecepcionInicio from "../GeneralComponents/ModalRecepcionInicio";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { NumericFormat } from "react-number-format";

import GeneralContext from "../GeneralContext/GeneralContext";
import SnackbarContext from "../GeneralContext/SnackbarContext";
import axios from "../../api/axios";

import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import PageButtonSimple from "../GeneralComponents/PageButtonSimple";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import InputFile from "../GeneralComponents/InputFile";
import BreadcrumbStandard from "../GeneralComponents/BreadcrumbStandard";
import InputFilter from "../GeneralComponents/InputFilter";
import InputChipSelect from "../GeneralComponents/InputChipSelect";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { esES } from "@mui/x-date-pickers/locales";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/es";

const CONVOCATORIA_OPTIONS = [
  "CPE2030-1",
  "CPE2030-2",
  "CPE2030-3",
  "CPE2030-4",
  "NYA 2021",
  "NYA 2022",
  "NYA 2023",
  "NYA 2024",
];

function fmtNA(v) {
  return v == null || v === "" ? "N/A" : String(v);
}
function fmtZona(v) {
  return v == null ? "N/A" : `Zona ${v}`;
}

function buildGroupTitle(keys, partsMap) {
  const mapLabel = {
    convocatoria: "Convocatoria",
    zona: "Zona",
    etapa: "Etapa",
    bloque: "Bloque",
  };

  const title = keys
    .map((k) => `${mapLabel[k]} ${partsMap[k] == null ? "N/A" : partsMap[k]}`)
    .join(" - ");

  return title ? `${title} -` : "";
}

function groupRows(rows, enabledKeys) {
  const map = new Map();

  for (const r of rows) {
    const key = enabledKeys
      .map((k) => `${k}:${r?.[k] == null ? "N/A" : String(r[k])}`)
      .join("|");

    if (!map.has(key)) {
      const partsMap = {};
      enabledKeys.forEach((k) => (partsMap[k] = r?.[k] ?? null));
      map.set(key, { id: key, partsMap, rows: [] });
    }
    map.get(key).rows.push(r);
  }

  const groups = Array.from(map.values());
  groups.sort((a, b) => a.id.localeCompare(b.id, "es", { numeric: true }));
  return groups;
}

function matchRowByFilters(row, filters) {
  if (filters.convocatoria == null) return false;

  if (filters.convocatoria != null && String(row.convocatoria) !== String(filters.convocatoria))
    return false;
  if (filters.operador != null && String(row.operador) !== String(filters.operador)) return false;

  if (filters.zona != null) {
    if (row.zona == null) return false;
    if (Number(row.zona) !== Number(filters.zona)) return false;
  }

  if (filters.etapa != null) {
    if (filters.etapa === "N/A") {
      if (row.etapa != null) return false;
    } else {
      if (row.etapa == null) return false;
      if (String(row.etapa) !== String(filters.etapa)) return false;
    }
  }

  if (filters.bloque != null) {
    if (filters.bloque === "N/A") {
      if (row.bloque != null) return false;
    } else {
      if (row.bloque == null) return false;
      if (String(row.bloque) !== String(filters.bloque)) return false;
    }
  }

  return true;
}

function flattenConvocatoriaToRows(payload) {
  const conv = payload?.convocatoria;
  const convId = conv?.id ?? null;

  const rows = [];
  const toYesNo = (b) => (b ? "Si" : "No");

  for (const op of conv?.operadores ?? []) {
    const operadorNombre = op?.nombre ?? "N/A";

    for (const z of op?.zonas ?? []) {
      const zonaId = z?.id ?? null;

      for (const e of z?.etapas ?? []) {
        const etapaId = e?.id ?? null;

        for (const b of e?.bloques ?? []) {
          const bloqueId = b?.id ?? null;

          for (const r of b?.rbds ?? []) {
            rows.push({
              id: `${convId}|${operadorNombre}|${zonaId}|${etapaId}|${bloqueId}|${r?.rbd}`,
              rbd: String(r?.rbd ?? ""),
              operador: operadorNombre,
              convocatoria: convId,
              zona: zonaId,
              etapa: etapaId,
              bloque: bloqueId,
              fas: String(r?.fas ?? "-"),
              recepcion_obras: toYesNo(Boolean(r?.rxo)),
            });
          }
        }

        for (const r of e?.rbds_sin_bloque ?? []) {
          rows.push({
            id: `${convId}|${operadorNombre}|${zonaId}|${etapaId}|NA|${r?.rbd}`,
            rbd: String(r?.rbd ?? ""),
            operador: operadorNombre,
            convocatoria: convId,
            zona: zonaId,
            etapa: etapaId,
            bloque: null,
            fas: String(r?.fas ?? "-"),
            recepcion_obras: toYesNo(Boolean(r?.rxo)),
          });
        }
      }
    }
  }

  return rows;
}

function flattenAnyPayloadToRows(payload) {
  if (!payload) return [];
  if (payload?.convocatoria) return flattenConvocatoriaToRows(payload);

  const list =
    payload?.convocatorias ??
    payload?.data ??
    (Array.isArray(payload) ? payload : null);

  if (Array.isArray(list)) {
    return list.flatMap((c) => {
      if (c?.convocatoria) return flattenConvocatoriaToRows(c);
      return flattenConvocatoriaToRows({ convocatoria: c });
    });
  }

  return [];
}

const alertCellSx = { backgroundColor: "#FFCACA" };

function isNotNo(v) {
  return String(v ?? "").trim().toLowerCase() !== "no";
}

function isNotAprobado(v) {
  return String(v ?? "").trim().toLowerCase() !== "aprobado";
}

function buildGroupIdFromRow(row, enabledKeys) {
  return enabledKeys
    .map((k) => `${k}:${row?.[k] == null ? "N/A" : String(row[k])}`)
    .join("|");
}

function toIsoNoZ(d) {
  if (!d) return null;
  try {
    return d.format("YYYY-MM-DDTHH:mm:ss");
  } catch {
    return null;
  }
}

function buildRbdsPayloadFromTables(searchRows, enabledGroupKeys, groupDevengo, fechaOficioIso) {
  return (searchRows ?? []).map((r) => {
    const groupId = buildGroupIdFromRow(r, enabledKeysSafe(enabledGroupKeys));
    const gState = groupDevengo?.[groupId] ?? { devengo: false, fecha: null };

    return {
      rbd: String(r?.rbd ?? ""),
      fecha_oficio: fechaOficioIso,
      devengo: Boolean(gState.devengo),
      fecha_inicio_devengo: gState.devengo ? toIsoNoZ(gState.fecha) : null,
      convocatoria: r?.convocatoria ?? null,
      operador: r?.operador ?? null,
      zona: r?.zona ?? null,
      etapa: r?.etapa ?? null,
      bloque: r?.bloque ?? null,
      fas: r?.fas ?? null,
      recepcion_obras: r?.recepcion_obras ?? null,
    };
  });
}

function enabledKeysSafe(keys) {
  return Array.isArray(keys) ? keys : [];
}

export default function ParqueInicioNew() {
  const { pageTitle, setPageTitle, setDrawerHidding } = useContext(GeneralContext);
  const openSnackbar = useContext(SnackbarContext);

  const [loading, setLoading] = useState(false);
  const [loadingConvocatoria, setLoadingConvocatoria] = useState(true);

  const [allRows, setAllRows] = useState([]);
  const [searchRows, setSearchRows] = useState([]);
  const inicioFileRef = useRef(null);

  const [filters, setFilters] = useState({
    convocatoria: null,
    operador: null,
    zona: null,
    etapa: null,
    bloque: null,
  });

  const [filtersResetSeq, setFiltersResetSeq] = useState(0);

  const [rbdSelectedList, setRbdSelectedList] = useState([]);
  const [rbdClearSeq, setRbdClearSeq] = useState(0);

  const hasConvocatoria = Boolean(filters.convocatoria);

  const [grouping, setGrouping] = useState({
    convocatoria: false,
    zona: true,
    etapa: true,
    bloque: false,
  });



  const enabledGroupKeys = useMemo(() => {
    const keys = [];
    if (grouping.convocatoria) keys.push("convocatoria");
    if (grouping.zona) keys.push("zona");
    if (grouping.etapa) keys.push("etapa");
    if (grouping.bloque) keys.push("bloque");
    return keys;
  }, [grouping]);

  const enabledGroupKeySet = useMemo(() => new Set(enabledGroupKeys), [enabledGroupKeys]);
  const groups = useMemo(
    () => groupRows(searchRows, enabledGroupKeys),
    [searchRows, enabledGroupKeys]
  );

  const [groupDevengo, setGroupDevengo] = useState({});
  const [formData, setFormData] = useState({
    rbd_list: [],
    oficio_inicio_servicio: "",
    fecha_inicio_servicio: dayjs().hour(0).minute(0).second(0),
  });

  const [evErrorInicio, setEvErrorInicio] = useState(false);
  const [inicioFile, setInicioFile] = useState();

  const links = [
    { name: "Estado de Parque", path: "/parque_lista" },
    { name: "Inicio de Servicio", path: "/parque_inicio" },
  ];

  const convocatoriaRows = useMemo(
    () => CONVOCATORIA_OPTIONS.map((id) => ({ convocatoria: id })),
    []
  );

  useEffect(() => {
    setPageTitle("Listado de reportes");
  }, [pageTitle, setPageTitle]);

  useEffect(() => {
    setDrawerHidding(false);
  }, [setDrawerHidding]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingConvocatoria(true);
        const resp = await axios.getByConvocatoria();
        const payload = resp?.data ?? resp;
        const rows = flattenAnyPayloadToRows(payload);
        if (!alive) return;
        setAllRows(rows);
      } catch (e) {
        if (!alive) return;
        setAllRows([]);
        openSnackbar?.("No se pudo cargar la data inicial.", "error");
      } finally {
        if (alive) setLoadingConvocatoria(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!filters.convocatoria) {
      setFilters((p) => ({ ...p, operador: null, zona: null, etapa: null, bloque: null }));
    } else {
      setFilters((p) => ({ ...p, operador: null, zona: null, etapa: null, bloque: null }));
    }

    setSearchRows([]);
    setGroupDevengo({});
    setFormData((p) => ({ ...p, rbd_list: [] }));
    setRbdSelectedList([]);
    setRbdClearSeq((s) => s + 1);
  }, [filters.convocatoria]);

  useEffect(() => {
    setFilters((p) => {
      if (p.zona == null && p.etapa == null && p.bloque == null) return p;
      return { ...p, zona: null, etapa: null, bloque: null };
    });
  }, [filters.operador]);

  useEffect(() => {
    setFilters((p) => {
      if (p.etapa == null && p.bloque == null) return p;
      return { ...p, etapa: null, bloque: null };
    });
  }, [filters.zona]);

  useEffect(() => {
    setFilters((p) => {
      if (p.bloque == null) return p;
      return { ...p, bloque: null };
    });
  }, [filters.etapa]);

  const onFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value ?? null }));
  };

  const setFileInicio = (archivo) => {
    setEvErrorInicio(!archivo ? true : false);
    setInicioFile((prevData) => ({ ...prevData, archivo }));
  };

  const rowsByConvocatoria = useMemo(() => {
    if (!filters.convocatoria) return [];
    return allRows.filter((r) => String(r.convocatoria) === String(filters.convocatoria));
  }, [allRows, filters.convocatoria]);

  const operadorData = useMemo(() => rowsByConvocatoria, [rowsByConvocatoria]);

  const zonaData = useMemo(() => {
    if (!filters.operador) return rowsByConvocatoria;
    return rowsByConvocatoria.filter((r) => String(r.operador) === String(filters.operador));
  }, [rowsByConvocatoria, filters.operador]);

  const etapaData = useMemo(() => {
    if (filters.operador && filters.zona != null) {
      return rowsByConvocatoria.filter(
        (r) =>
          String(r.operador) === String(filters.operador) &&
          Number(r.zona) === Number(filters.zona)
      );
    }
    if (filters.operador)
      return rowsByConvocatoria.filter((r) => String(r.operador) === String(filters.operador));
    if (filters.zona != null)
      return rowsByConvocatoria.filter(
        (r) => r.zona != null && Number(r.zona) === Number(filters.zona)
      );
    return rowsByConvocatoria;
  }, [rowsByConvocatoria, filters.operador, filters.zona]);
    const invalidRows = useMemo(() => {
  const notAprobado = [];
  const noRecepcion = [];

  for (const r of searchRows) {
    if (isNotAprobado(r?.fas)) notAprobado.push(String(r?.rbd ?? ""));
    if (!isNotNo(r?.recepcion_obras)) noRecepcion.push(String(r?.rbd ?? ""));
  }

  return { notAprobado, noRecepcion };
}, [searchRows]);

const disableSubmitByRules =
  formData.rbd_list.length === 0 ||
  invalidRows.notAprobado.length > 0 ||
  invalidRows.noRecepcion.length > 0;

const disableReasonMsg = useMemo(() => {
  const msgs = [];
  if (invalidRows.notAprobado.length > 0) {
    msgs.push(`Hay ${invalidRows.notAprobado.length} RBD(s) con FAS distinto de "Aprobado".`);
  }
  if (invalidRows.noRecepcion.length > 0) {
    msgs.push(
      `Hay ${invalidRows.noRecepcion.length} RBD(s) con "Recepción de obras" distinto de "No".`
    );
  }
  return msgs.join(" ");
}, [invalidRows]);

  const bloqueData = useMemo(() => {
    const etapaVal = filters.etapa;
    return rowsByConvocatoria.filter((r) => {
      if (filters.operador && String(r.operador) !== String(filters.operador)) return false;
      if (filters.zona != null && Number(r.zona) !== Number(filters.zona)) return false;

      if (etapaVal != null) {
        if (etapaVal === "N/A") {
          if (r.etapa != null) return false;
        } else {
          if (String(r.etapa ?? "") !== String(etapaVal)) return false;
        }
      }
      return true;
    });
  }, [rowsByConvocatoria, filters.operador, filters.zona, filters.etapa]);

  const rbdData = useMemo(() => {
    return allRows;
  }, [allRows]);
  const groupBg = "rgba(25, 118, 210, 0.16)";
  const groupCellSx = (colKey) =>
    enabledGroupKeySet.has(colKey) ? { backgroundColor: groupBg } : undefined;

  const removeBtnSx = {
    padding: 0,
    "& svg": { fontSize: 20, color: "rgba(0,0,0,0.70)" },
    "&:hover svg": { color: "#D84315" },
  };

  const removeRbdsFromLists = (rbdsToRemove) => {
    const removeSet = new Set(rbdsToRemove.map(String));
    setSearchRows((prev) => prev.filter((r) => !removeSet.has(String(r.rbd))));
    setFormData((prev) => ({
      ...prev,
      rbd_list: prev.rbd_list.filter((rbd) => !removeSet.has(String(rbd))),
    }));
  };

  const handleRemoveGroup = (groupId) => {
    const g = groups.find((x) => x.id === groupId);
    if (!g) return;

    removeRbdsFromLists(g.rows.map((r) => r.rbd));

    setGroupDevengo((prev) => {
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
  };

  const handleRemoveRow = (rbd) => {
    removeRbdsFromLists([rbd]);
  };

  const addManyRbds = (rbdList) => {
    const rbds = (rbdList ?? []).map(String).filter(Boolean);
    if (rbds.length === 0) return;

    const foundRows = [];
    for (const rbd of rbds) {
      const found = allRows.find((r) => String(r.rbd) === String(rbd));
      if (found) foundRows.push(found);
    }

    if (!foundRows.length) {
      openSnackbar?.("No se encontraron RBD en los datos precargados.", "error");
      return;
    }

    setSearchRows((prev) => {
      const set = new Set(prev.map((x) => String(x.rbd)));
      const add = foundRows.filter((x) => !set.has(String(x.rbd)));
      if (!add.length) return prev;
      return [...prev, ...add];
    });

    setFormData((prev) => {
      const set = new Set(prev.rbd_list.map(String));
      const add = foundRows.map((x) => String(x.rbd)).filter((rbd) => !set.has(String(rbd)));
      if (!add.length) return prev;
      return { ...prev, rbd_list: [...prev.rbd_list, ...add] };
    });
  };

  const handleBuscarIzquierda = () => {
    if (!filters.convocatoria) {
      openSnackbar?.(
        "Debes seleccionar una convocatoria antes de filtrar por operador/zona/etapa/bloque.",
        "warning"
      );
      return;
    }

    const matches = rowsByConvocatoria.filter((r) => matchRowByFilters(r, filters));
    if (!matches.length) {
      openSnackbar?.("No hay resultados con los filtros seleccionados.", "warning");
      return;
    }

    setSearchRows((prev) => {
      const prevSet = new Set(prev.map((x) => String(x.rbd)));
      const add = matches.filter((m) => !prevSet.has(String(m.rbd)));
      if (add.length === 0) return prev;
      return [...prev, ...add];
    });

    setFormData((prev) => {
      const prevSet = new Set(prev.rbd_list.map(String));
      const add = matches.map((m) => String(m.rbd)).filter((rbd) => !prevSet.has(String(rbd)));
      if (!add.length) return prev;
      return { ...prev, rbd_list: [...prev.rbd_list, ...add] };
    });
  };

  const handleBuscarRbdDerecha = () => {

    if (!rbdSelectedList.length) {
      openSnackbar?.("Debes agregar al menos un RBD a la lista.", "warning");
      return;
    }

    addManyRbds(rbdSelectedList);

    setRbdSelectedList([]);
    setRbdClearSeq((s) => s + 1);
  };

  const handleLimpiarFiltros = () => {
    setFilters({
      convocatoria: null,
      operador: null,
      zona: null,
      etapa: null,
      bloque: null,
    });

    setFiltersResetSeq((s) => s + 1);

    setRbdSelectedList([]);
    setRbdClearSeq((s) => s + 1);

    setSearchRows([]);
    setGroupDevengo({});
    setFormData((p) => ({ ...p, rbd_list: [] }));
  };

    const [open, setOpen] = React.useState(false);
  const [allData, setAllData] = React.useState([]);
  const handleOpen = () => {setOpen(true);}
  const handleClose = (fromModal, successfulCount) =>{ setOpen(false); }
  const handleSend = (event) => {
    event.preventDefault();

    if (!inicioFile?.archivo) {
      setEvErrorInicio(true);
      inicioFileRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const fechaOficioIso = formData.fecha_inicio_servicio?.format("YYYY-MM-DDTHH:mm:ss");
    const rbdsFromTables = buildRbdsPayloadFromTables(
      searchRows,
      enabledGroupKeys,
      groupDevengo,
      fechaOficioIso
    );

    if (!rbdsFromTables.length) return;
    const updatedFormData = {
      url_oficio: "",
      descripcion: "",
      numero_oficio: formData.oficio_inicio_servicio,
      fecha_oficio: fechaOficioIso,
      tipo_oficio: "INICIO_SERVICIO",
      agrupacion: {
        switches: grouping,
        enabled_keys: enabledGroupKeys,
      },
      rbds: rbdsFromTables,
    };
    setAllData(updatedFormData);
    setOpen(true);
  }


  if (loadingConvocatoria) {
    return (
      <PageBody>
        <BreadcrumbStandard links={links} />
        <PageBgBlock>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
              width: "100%",
              backgroundColor: "white",
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Cargando...
            </Typography>
          </Box>
        </PageBgBlock>
      </PageBody>
    );
  }

  return (
    <PageBody>
      <BreadcrumbStandard links={links} />
      <PageBgBlock>
        <form onSubmit={handleSend} id="parque_inicio_servicio">
          <Stack spacing={2}>
            <Typography variant="h1" sx={{ textAlign: "center", fontWeight: 700 }}>
              INICIO SERVICIO
            </Typography>

            <Typography variant="body" sx={{ textAlign: "center" }}>
              Primero se debe dejar el documento de Oficio, su número de oficio y fecha de inicio de
              servicio. A continuación se debe seleccionar por convocatoria, por zona, por etapa,
              bloque o por RBD (ya sea por si solos) para otorgarles Inicio de Servicio. Por último
              debe marcar la selección si esta queda en estado de Devengo.
            </Typography>

            <Divider />

            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Datos del Oficio relacionado al Inicio de Servicio
            </Typography>

            <Grid container spacing={2}>
              <Grid size={12}>
                <Box ref={inicioFileRef}>
                  <InputFile
                    onFileSelect={setFileInicio}
                    fileType="pdf"
                    placeholder="Oficio inicio de servicio"
                    buttonText="Subir archivo"
                    sizeBtnGrid={3}
                  />
                  {evErrorInicio && (
                    <Typography variant="caption" sx={{ color: "red" }}>
                      Debe adjuntar oficio
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid size={6}>
                <FormControl sx={{ width: "100%" }} size="small">
                  <NumericFormat
                    required
                    customInput={TextField}
                    label="N° de oficio inicio de servicio"
                    value={formData.oficio_inicio_servicio}
                    onValueChange={(newValue) =>
                      setFormData((prev) => ({ ...prev, oficio_inicio_servicio: newValue.value }))
                    }
                    allowNegative={false}
                    size="small"
                    sx={{ width: "100%" }}
                  />
                </FormControl>
              </Grid>

              <Grid size={6}>
                <FormControl fullWidth variant="outlined">
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={"es"}
                    localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}
                  >
                    <DateTimePicker
                      label="Fecha inicio de servicio"
                      slotProps={{ textField: { size: "small" } }}
                      value={formData.fecha_inicio_servicio}
                      sx={{ width: "100%" }}
                      onChange={(newValue) =>
                        setFormData((prev) => ({ ...prev, fecha_inicio_servicio: newValue }))
                      }
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Filtros
            </Typography>

            <Grid container spacing={1}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, height: "100%" }}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      FIltros por grupo
                    </Typography>

                    <InputFilter
                      key={`convocatoria-${filtersResetSeq}`}
                      item="convocatoria"
                      name="Buscar Convocatoria"
                      data={convocatoriaRows}
                      callback={onFilterChange}
                    />

                    <InputFilter
                      key={`operador-${filtersResetSeq}`}
                      item="operador"
                      name="Buscar Operador"
                      data={operadorData}
                      callback={onFilterChange}
                      disabled={!filters.convocatoria}
                    />
                    <InputFilter
                      key={`zona-${filtersResetSeq}`}
                      item="zona"
                      name="Buscar Zona"
                      data={zonaData}
                      callback={onFilterChange}
                      disabled={!filters.convocatoria}
                    />
                    <InputFilter
                      key={`etapa-${filtersResetSeq}`}
                      item="etapa"
                      name="Buscar Etapa"
                      data={etapaData}
                      callback={onFilterChange}
                      defaultOpt={["N/A"]}
                      disabled={!filters.convocatoria}
                    />
                    <InputFilter
                      key={`bloque-${filtersResetSeq}`}
                      item="bloque"
                      name="Buscar Bloque"
                      data={bloqueData}
                      callback={onFilterChange}
                      defaultOpt={["N/A"]}
                      disabled={!filters.convocatoria}
                    />

                    <PageButtonWhite
                      pad=".2rem"
                      width="100%"
                      onClick={handleBuscarIzquierda}
                      icon={<SearchIcon />}
                      disabled={!hasConvocatoria}
                    >
                      Cargar
                    </PageButtonWhite>
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, height: "100%" }}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Filtro por RBD
                    </Typography>

                    <InputChipSelect
                      item="rbd"
                      name="Buscar RBD"
                      data={rbdData}
                      value={rbdSelectedList}
                      callback={(item, values) => {
                        if (item === "rbd") setRbdSelectedList((values ?? []).map(String));
                      }}
                      clearSignal={rbdClearSeq}
                      hideClearButton
                      showLeftBorder={false}
                    />

                    <PageButtonWhite
                      pad=".2rem"
                      width="100%"
                      onClick={handleBuscarRbdDerecha}
                      icon={<SearchIcon />}
                    >
                      Cargar
                    </PageButtonWhite>
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={12}>
                <PageButtonWhite
                  pad=".2rem"
                  width="100%"
                  border="#D84315"
                  color="#D84315"
                  onClick={handleLimpiarFiltros}
                  icon={<RemoveCircleOutlineIcon />}
                >
                  Limpiar todos los filtros
                </PageButtonWhite>
              </Grid>
            </Grid>

            <Divider sx={{ pt: 2 }} />

            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={grouping.convocatoria}
                    onChange={(e) => setGrouping((p) => ({ ...p, convocatoria: e.target.checked }))}
                  />
                }
                label="Agrupar por Convocatoria"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={grouping.zona}
                    onChange={(e) => setGrouping((p) => ({ ...p, zona: e.target.checked }))}
                  />
                }
                label="Agrupar por Zona"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={grouping.etapa}
                    onChange={(e) => setGrouping((p) => ({ ...p, etapa: e.target.checked }))}
                  />
                }
                label="Agrupar por Etapa"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={grouping.bloque}
                    onChange={(e) => setGrouping((p) => ({ ...p, bloque: e.target.checked }))}
                  />
                }
                label="Agrupar por Bloque"
              />
            </Stack>

            <Box>
              {groups.map((g) => {
                const title = buildGroupTitle(enabledKeysSafe(enabledGroupKeys), g.partsMap);
                const state = groupDevengo[g.id] ?? { devengo: false, fecha: null };

                return (
                  <Box key={g.id} sx={{ mt: 2, pb: 2 }}>
                    <Paper variant="outlined" sx={{ p: 0, mb: 2 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell colSpan={8} sx={{ fontWeight: 700 }}>
                                {title}{" "}
                                <Box
                                  component="span"
                                  sx={{ fontWeight: 500, color: "rgba(0,0,0,0.70)" }}
                                >
                                  Total RBD: {g.rows.length}
                                </Box>
                              </TableCell>
                              <TableCell width={200}>
                                <PageButtonSimple
                                  width={"100%"}
                                  style={"warning"}
                                  startIcon={<RemoveCircleOutlineIcon />}
                                  onClick={() => handleRemoveGroup(g.id)}
                                >
                                  Eliminar Lista
                                </PageButtonSimple>
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell width={40}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveGroup(g.id)}
                                  sx={removeBtnSx}
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </TableCell>
                              <TableCell>RBD</TableCell>
                              <TableCell>Operador</TableCell>
                              <TableCell sx={groupCellSx("convocatoria")}>Convocatoria</TableCell>
                              <TableCell sx={groupCellSx("zona")}>Zona</TableCell>
                              <TableCell sx={groupCellSx("etapa")}>Etapa</TableCell>
                              <TableCell sx={groupCellSx("bloque")}>Bloque</TableCell>
                              <TableCell>FAS</TableCell>
                              <TableCell>Recepción de obras</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {g.rows.map((r) => (
                              <TableRow key={r.rbd}>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveRow(r.rbd)}
                                    sx={removeBtnSx}
                                  >
                                    <RemoveCircleOutlineIcon />
                                  </IconButton>
                                </TableCell>

                                <TableCell>{fmtNA(r.rbd)}</TableCell>
                                <TableCell>{fmtNA(r.operador)}</TableCell>

                                <TableCell sx={groupCellSx("convocatoria")}>
                                  {fmtNA(r.convocatoria)}
                                </TableCell>
                                <TableCell sx={groupCellSx("zona")}>{fmtZona(r.zona)}</TableCell>
                                <TableCell sx={groupCellSx("etapa")}>{fmtNA(r.etapa)}</TableCell>
                                <TableCell sx={groupCellSx("bloque")}>{fmtNA(r.bloque)}</TableCell>

                                <TableCell sx={isNotAprobado(r.fas) ? alertCellSx : undefined}>
                                  {fmtNA(r.fas)}
                                </TableCell>
                                <TableCell sx={isNotNo(r.recepcion_obras) ? undefined : alertCellSx}>
                                  {fmtNA(r.recepcion_obras)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale={"es"}
                      localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}
                    >
                      <Stack direction="row" spacing={2} sx={{ mt: 1, alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={state.devengo}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setGroupDevengo((prev) => ({
                                  ...prev,
                                  [g.id]: {
                                    devengo: checked,
                                    fecha: checked ? (state.fecha ?? dayjs()) : null,
                                  },
                                }));
                              }}
                            />
                          }
                          label="Inicio Devengo"
                        />

                        <DatePicker
                          label="Fecha inicio Devengo"
                          value={state.fecha}
                          disabled={!state.devengo}
                          slotProps={{ textField: { size: "small" } }}
                          onChange={(newValue) => {
                            setGroupDevengo((prev) => ({
                              ...prev,
                              [g.id]: { ...state, fecha: newValue },
                            }));
                          }}
                        />
                      </Stack>
                    </LocalizationProvider>
                  </Box>
                );
              })}
            </Box>
{formData.rbd_list.length > 0 && disableSubmitByRules && (
  <Alert
    severity="error"
    icon={<ErrorOutlineIcon sx={{fontSize: 32}} />}
    sx={{
      mt: 1.5,
      backgroundColor: "#FDECEA",   
      color: "#611A15",
      alignItems: "center",       
    }}
  >
    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      No puedes ingresar el Inicio de Servicio porque:
    </Typography>

    <Box component="ul" sx={{ pl: 3, mb: 0 }}>
      {invalidRows.notAprobado.length > 0 && (
        <li>
          <Typography variant="body2">
            Hay {invalidRows.notAprobado.length} RBD(s) con FAS no <b>"Aprobado"</b>.
          </Typography>
        </li>
      )}

      {invalidRows.noRecepcion.length > 0 && (
        <li>
          <Typography variant="body2">
            Hay {invalidRows.noRecepcion.length} RBD(s) sin <b>"Recepción de obras"</b>.
          </Typography>
        </li>
      )}
    </Box>
  </Alert>
)}

            <Box sx={{ margin: "auto", marginTop: "1.5rem" }}>
              <PageButtonBlack
                form={"parque_inicio_servicio"}
                loading={loading}
                disabled={disableSubmitByRules}
                style="success"
                bgColor={disableSubmitByRules ? "" : "#C8E6C9"}
              >
                Ingresar Inicio de Servicio
              </PageButtonBlack>
            </Box>
          </Stack>
        </form>
      </PageBgBlock>
                    <ModalRecepcionInicio
          open={open}
          oficioFile={inicioFile}
          data={allData}
          onClose={handleClose}
        ></ModalRecepcionInicio>
    </PageBody>
  );
}
